// src/features/export/exportFormats/jpegExport.ts
import { toJpeg } from 'html-to-image';
import { Model } from '../../../types';
import {
    ExportHandler,
    ExportResult,
    ExportConfig,
    ExportProgressCallback,
    ExportQuality,
    toExportQuality,
} from '../exportTypes';
import {
    generateFilename,
    createExportMetadata,
} from '../exportUtils';
import { beginVisualExport } from '../exportPreparation';

export class JPEGExportHandler implements ExportHandler {
    public readonly format = 'jpeg' as const;

    /**
     * Detect current theme (dark/light mode)
     */
    private detectCurrentTheme(): 'dark' | 'light' {
        if (document.documentElement.classList.contains('dark')) return 'dark';
        if (document.body.classList.contains('dark')) return 'dark';
        const muiTheme = document.querySelector('[data-mui-color-scheme]');
        if (muiTheme?.getAttribute('data-mui-color-scheme') === 'dark') return 'dark';
        if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
        return 'light';
    }

    /**
     * Get theme-appropriate background color (JPEG always needs a background)
     */
    private getThemeBackgroundColor(userColor?: string): string {
        if (userColor) return userColor;

        // Auto-detect theme and return appropriate color
        const theme = this.detectCurrentTheme();
        return theme === 'dark' ? '#1e293b' : '#ffffff';
    }

    async export(
        element: HTMLElement,
        model: Model,
        config: ExportConfig,
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        try {
            onProgress?.(0, 'Starting JPEG export...');

            const quality: ExportQuality = config.quality ? toExportQuality(config.quality) : 2;
            const scale = quality;

            onProgress?.(20, 'Preparing canvas...');

            const backgroundColor = this.getThemeBackgroundColor(config.backgroundColor);
            const restoreExportState = beginVisualExport(element, backgroundColor);

            // 🔥 CRITICAL: Inject CSS to hide UI elements while preserving label borders
            const removeHideStyles = this.injectHideStyles();
            const cleanup = () => {
                removeHideStyles();
                restoreExportState();
            };

            // Wait for React to re-render and styles to apply
            await new Promise(resolve => setTimeout(resolve, 500));

            onProgress?.(50, 'Capturing diagram...');

            // Suppress console warnings
            const originalError = console.error;
            const suppressedPatterns = [
                'chrome-extension://',
                'fabric-icons',
                'leelawadeeui',
                'font',
                'Status:0',
                'Status:404',
                'ERR_FILE_NOT_FOUND'
            ];

            console.error = (...args: any[]) => {
                const msg = args.join(' ').toString();
                const shouldSuppress = suppressedPatterns.some(pattern =>
                    msg.toLowerCase().includes(pattern.toLowerCase())
                );
                if (!shouldSuppress) {
                    originalError(...args);
                }
            };

            try {
                // Get background color with theme auto-detection (JPEG always needs background)
                const dataUrl = await toJpeg(element, {
                    cacheBust: true,
                    quality: 0.95,
                    pixelRatio: scale,
                    width: element.offsetWidth,
                    height: element.offsetHeight,
                    backgroundColor: backgroundColor,
                    // ✨ CRITICAL: Filter function to exclude UI elements
                    filter: (node: HTMLElement) => {
                        // Skip non-element nodes
                        if (!node.classList) return true;

                        // Exclude grid if includeGrid is false
                        if (!config.includeGrid && node.getAttribute('data-grid') === 'true') {
                            return false;
                        }

                        // Exclude elements with these classes
                        const excludeClasses = [
                            'MuiBackdrop',
                            'MuiDialog',
                            'transform-control',
                            'selection-box',
                            'resize-handle',
                            'anchor-circle',
                            'control-point',
                            'connector-handle'
                        ];

                        for (const cls of excludeClasses) {
                            if (node.classList.contains(cls)) {
                                return false;
                            }
                        }

                        // Exclude by data attributes
                        if (node.getAttribute('data-element') === 'transform-control') return false;
                        if (node.getAttribute('data-element') === 'cursor') return false;
                        if (node.getAttribute('data-interaction-layer') === 'true') return false;

                        // Exclude hidden elements
                        if (node.style.display === 'none') return false;
                        if (node.style.visibility === 'hidden') return false;
                        if (node.style.opacity === '0') return false;

                        return true;
                    },
                    // Style overrides to ensure clean export
                    style: {
                        // Remove any inherited transform
                        transform: 'none'
                    }
                });

                console.error = originalError;
                cleanup();

                onProgress?.(70, 'Processing image...');

                // Convert data URL to blob
                const response = await fetch(dataUrl);
                const blob = await response.blob();

                onProgress?.(90, 'Finalizing...');

                // Generate filename
                const filename = config.filename || generateFilename(model.title || 'flowi-diagram', 'jpeg');
                const metadata = createExportMetadata(
                    config,
                    element.offsetWidth,
                    element.offsetHeight,
                    model.title
                );

                onProgress?.(100, 'Export complete!');

                return {
                    success: true,
                    data: blob,
                    filename,
                    format: 'jpeg',
                    metadata,
                };
            } finally {
                console.error = originalError;
                cleanup();
            }
        } catch (error) {
            console.error('❌ JPEG export failed:', error);
            return {
                success: false,
                filename: '',
                error: error as Error,
            };
        }
    }

    /**
     * Inject CSS to hide UI elements during export while preserving label borders
     */
    private injectHideStyles(): () => void {
        const styleEl = document.createElement('style');
        styleEl.id = 'flowi-export-cleanup';
        styleEl.textContent = `
            /* ============ HIDE TRANSFORM CONTROLS & UI ELEMENTS ============ */
            [data-element="transform-control"],
            [data-element="cursor"],
            [data-interaction-layer="true"],
            .MuiBackdrop-root,
            .MuiDialog-root,
            .transform-control,
            .selection-box,
            .resize-handle,
            .anchor-circle,
            .control-point,
            .connector-handle {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
            }

            /* ============ REMOVE BORDERS FROM ICONS & IMAGES (NOT LABELS) ============ */
            img:not([data-flowi-label] img):not(.flowi-label img) {
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
                box-sizing: content-box !important;
            }

            /* ============ TARGET SVG ELEMENTS (NOT LABELS) ============ */
            svg:not([data-flowi-label] svg):not(.flowi-label svg) {
                border: none !important;
                outline: none !important;
            }

            svg:not([data-flowi-label] svg) rect[stroke],
            svg:not([data-flowi-label] svg) circle[stroke],
            svg:not([data-flowi-label] svg) path[stroke] {
                stroke: none !important;
            }

            /* Remove selection rectangles (white with gray borders) */
            svg rect[fill="#ffffff"][stroke="rgb(189, 189, 189)"],
            svg rect[fill="rgb(255, 255, 255)"][stroke="rgb(189, 189, 189)"],
            svg rect[fill="#fff"][stroke="#bdbdbd"],
            svg rect[stroke="rgb(189, 189, 189)"],
            svg rect[stroke="rgb(158, 158, 158)"],
            svg rect[stroke="#bdbdbd"],
            svg rect[stroke="#9e9e9e"] {
                display: none !important;
            }

            /* ============ HIDE CONNECTOR DOTS AND SELECTION ============ */
            svg[width="3"] line[stroke-dasharray],
            svg[viewBox*="0 0 3"] line,
            circle[fill="#4caf50"],
            circle[fill="rgb(76, 175, 80)"] {
                display: none !important;
            }

            /* ============ HIDE SELECTED/ACTIVE STATES (NOT LABELS) ============ */
            [data-selected="true"]:not([data-flowi-label]):not(.flowi-label),
            [aria-selected="true"]:not([data-flowi-label]):not(.flowi-label),
            .selected:not([data-flowi-label]):not(.flowi-label),
            .active:not([data-flowi-label]):not(.flowi-label),
            .highlighted:not([data-flowi-label]):not(.flowi-label) {
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
            }

            /* ============ FIX FOR POSITIONED ELEMENTS (NOT LABELS) ============ */
            [style*="position: absolute"] img:not([data-flowi-label] img):not(.flowi-label img),
            [style*="position: relative"] img:not([data-flowi-label] img):not(.flowi-label img) {
                border: none !important;
                outline: none !important;
            }

            /* ============ PRESERVE LABEL STYLING ============ */
            /* Labels already have borders defined - just don't remove them */
            /* No additional CSS needed - the exclusions above are sufficient */
        `;

        document.head.appendChild(styleEl);

        // Return cleanup function
        return () => {
            const style = document.getElementById('flowi-export-cleanup');
            if (style) {
                style.remove();
            }
        };
    }
}

export const createJPEGExportHandler = () => new JPEGExportHandler();
